import api from "@/config/api";
import { Forms } from "@/types/form";
import { ComplementStatus, Report } from "@/types/report";
import { Params } from "@/types/utils";
import { i18n } from "@/utils/i18n";
import { log } from "@/utils/logger";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

const useSubmit = () => {
    const { oid, sid, rid } = useParams<Params>();
    const queryClient = useQueryClient();

    const onSubmit = async (data: Partial<Forms["Report"]>, reset: () => void) => {
        try {
            const report = await api.patch<Report>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}`,
                { data },
            );

            log.success(i18n.t("saveReport", { scope: "messages.success" }));
            queryClient.setQueryData<Report>(["report", rid], report);
            queryClient.invalidateQueries({ queryKey: ["categories-overview", oid, sid, rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });

            reset();
        } catch (e) {
            log.error(e);
        }
    };

    return onSubmit;
};

const useSaveReviewNotes = () => {
    const { oid, sid, rid } = useParams<Params>();
    const queryClient = useQueryClient();
    const [saving, setSaving] = useState(false);

    const onSave = async (reviewNotes: Report["reviewNotes"]) => {
        try {
            setSaving(true);
            const report = await api.patch<Report>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}`,
                { data: { reviewNotes } },
            );

            log.success(i18n.t("updateReport", { scope: "messages.success" }));
            queryClient.setQueryData<Report>(["report", rid], report);
            queryClient.invalidateQueries({ queryKey: ["report", rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });

            return report;
        } catch (e) {
            log.error(e);
            return null;
        } finally {
            setSaving(false);
        }
    };

    return { saving, onSave };
};

const useAcknowledgeComplement = () => {
    const { oid, sid, rid } = useParams<Params>();
    const queryClient = useQueryClient();
    const [acknowledging, setAcknowledging] = useState(false);

    const onAcknowledge = async () => {
        try {
            setAcknowledging(true);
            const report = await api.patch<Report>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}`,
                { data: { complementStatus: ComplementStatus.Acknowledged } },
            );

            queryClient.setQueryData<Report>(["report", rid], report);
            queryClient.invalidateQueries({ queryKey: ["report", rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });
            log.success(i18n.t("complementAcknowledgedSuccess", { scope: "messages.success" }));
        } catch (e) {
            log.error(e);
        } finally {
            setAcknowledging(false);
        }
    };

    return { acknowledging, onAcknowledge };
};

const useToggleAttachmentCompletion = () => {
    const { oid, sid, rid } = useParams<Params>();
    const queryClient = useQueryClient();
    const [toggling, setToggling] = useState(false);

    const onToggle = async (currentStatus: ComplementStatus) => {
        try {
            setToggling(true);
            const nextStatus =
                currentStatus === ComplementStatus.Requested
                    ? ComplementStatus.None
                    : ComplementStatus.Requested;

            const report = await api.patch<Report>(
                `/organizations/${oid}/submissions/${sid}/reports/${rid}`,
                { data: { complementStatus: nextStatus } },
            );

            queryClient.setQueryData<Report>(["report", rid], report);
            queryClient.invalidateQueries({ queryKey: ["report", rid] });
            queryClient.invalidateQueries({ queryKey: ["reports", oid] });

            log.success(
                i18n.t(
                    nextStatus === ComplementStatus.Requested
                        ? "attachmentCompletionRequested"
                        : "attachmentCompletionCancelled",
                    { scope: "messages.success" },
                ),
            );
        } catch (e) {
            log.error(e);
        } finally {
            setToggling(false);
        }
    };

    return { toggling, onToggle };
};

export { useSubmit, useSaveReviewNotes, useAcknowledgeComplement, useToggleAttachmentCompletion };
